import { InteractiveContainer, PlainButton } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import React, { MouseEvent } from "react";
import { useVoiceAttributeLabels } from "../../hooks/useVoiceAttributeLabels";
import { VoiceAgentVoiceDetails } from "../../types";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as SoundActiveIcon } from "./assets/sound-active.svg";
import { ReactComponent as SoundIcon } from "./assets/sound.svg";
import { ReactComponent as StopIcon } from "./assets/stop.svg";

export interface VoiceAgentVoiceCardProps {
  voice: VoiceAgentVoiceDetails;
  isSelected: boolean;
  isPlaying: boolean;
  isDisabled?: boolean;
  hidePlayButton?: boolean;
  onSelect: () => void;
  onTogglePlay: () => void;
}

const VoiceAgentVoiceTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex h-7 min-w-0 items-center justify-center rounded-xl border border-solid border-grey-200 bg-grey-100 px-3 font-secondary text-xs font-medium text-dark-800">
    <span className="truncate">{label}</span>
  </span>
);

export const VoiceAgentVoiceCard: React.FC<VoiceAgentVoiceCardProps> = ({
  voice,
  isSelected,
  isPlaying,
  isDisabled,
  hidePlayButton,
  onSelect,
  onTogglePlay,
}) => {
  const { genderLabels, styleLabels } = useVoiceAttributeLabels();

  const handleTogglePlay = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onTogglePlay();
  };

  return (
    <InteractiveContainer
      isDisabled={isDisabled}
      className={cx(
        "group flex h-39 w-full max-w-65 flex-col rounded-xl bg-white px-3 py-4 transition ring-inset",
        isSelected
          ? "ring-1 ring-dark-700"
          : "ring-1 ring-dark-100 hover:ring-2",
        isDisabled && "pointer-events-none opacity-50"
      )}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        {isSelected ? <SoundActiveIcon /> : <SoundIcon />}
        <span className="grow truncate font-secondary text-sm font-medium text-black">
          {voice.id}
        </span>
        {!hidePlayButton && (
          <PlainButton
            className={cx(
              "flex h-7 w-7 items-center justify-center rounded-full bg-dark transition-opacity",
              isPlaying
                ? "opacity-100"
                : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
            )}
            onClick={handleTogglePlay}
          >
            {isPlaying ? <StopIcon /> : <PlayIcon />}
          </PlainButton>
        )}
      </div>
      <span className="mt-0.5 line-clamp-3 font-secondary text-[13px] leading-[150%] text-[#737B7E]">
        {voice.description}
      </span>
      <div className="mt-auto flex min-w-0 gap-1.5">
        <VoiceAgentVoiceTag label={genderLabels[voice.gender]} />
        <VoiceAgentVoiceTag label={styleLabels[voice.style]} />
      </div>
    </InteractiveContainer>
  );
};
